import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Database, BarChart3, PoundSterling, Wallet, KeyRound, Settings as SettingsIcon, LifeBuoy, LogOut, Trophy, FileText, Layout } from "lucide-react";
import SupportChatWidget from "@/components/SupportChatWidget";
import UnsignedContractBanner from "@/components/dashboard/UnsignedContractBanner";
import { MobileMenuButton, MobileNavDrawer, MobileBottomNav } from "@/components/dashboard/MobileNav";
import QrPhoneButton from "@/components/dashboard/QrPhoneButton";

/**
 * Publisher portal layout — mirrors the buyer DashboardLayout pattern so the
 * publisher portal feels equally polished (iter65). Mobile drawer + bottom-nav
 * added iter81 for tap-friendly portal navigation on phones.
 */
const NAV = [
  { to: "/publisher",              label: "Overview",   Icon: LayoutDashboard, end: true },
  { to: "/publisher/leads",        label: "My Leads",   Icon: Database },
  { to: "/publisher/sectors",      label: "Sectors",    Icon: BarChart3 },
  { to: "/publisher/earnings",     label: "Earnings",   Icon: PoundSterling },
  { to: "/publisher/payouts",      label: "Payouts",    Icon: Wallet },
  { to: "/publisher/api-keys",     label: "API & Keys", Icon: KeyRound },
  { to: "/publisher/landers",      label: "Landers",    Icon: Layout },
  { to: "/publisher/contracts",    label: "Contracts",  Icon: FileText },
  { to: "/publisher/leaderboard",  label: "Leaderboard",Icon: Trophy },
  { to: "/publisher/support",      label: "Support",    Icon: LifeBuoy },
  { to: "/publisher/settings",     label: "Settings",   Icon: SettingsIcon, group: "footer" },
];

// Mobile bottom-nav: 5 most-used routes for publishers
const BOTTOM_NAV = [
  { to: "/publisher",          label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/publisher/leads",    label: "Leads",    Icon: Database },
  { to: "/publisher/earnings", label: "Earnings", Icon: PoundSterling },
  { to: "/publisher/payouts",  label: "Payouts",  Icon: Wallet },
  { to: "/publisher/support",  label: "Support",  Icon: LifeBuoy },
];

export default function PublisherLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const handleLogout = React.useCallback(() => { logout(); nav("/login"); }, [logout, nav]);
  return (
    <div className="min-h-screen bg-slate-50/40 grid lg:grid-cols-[240px_1fr]" data-testid="publisher-layout">
      <aside className="hidden lg:flex flex-col border-r border-slate-200 bg-white px-4 py-5 sticky top-0 h-screen">
        <div className="px-2 mb-6">
          <div className="text-[17px] font-semibold tracking-tight text-slate-900">Connexis</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Publisher portal</div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.filter((n) => n.group !== "footer").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid={`pub-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}>
              <n.Icon className="size-4" strokeWidth={1.7} />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-3 space-y-1">
          {NAV.filter((n) => n.group === "footer").map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
              <n.Icon className="size-4" strokeWidth={1.7} />{n.label}
            </NavLink>
          ))}
          <div className="px-3 py-1"><QrPhoneButton /></div>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100"
            data-testid="pub-logout">
            <LogOut className="size-4" strokeWidth={1.7} />Sign out
          </button>
          <div className="px-3 pt-2 pb-1 text-[11px] text-slate-400 truncate">{user?.email}</div>
        </div>
      </aside>

      <main className="px-5 lg:px-10 py-6 lg:py-10 min-w-0 space-y-5">
        {/* Mobile top bar: hamburger + brand */}
        <div className="lg:hidden flex items-center justify-between -mx-5 px-4 -mt-6 mb-2 h-14 border-b border-slate-200 bg-white sticky top-0 z-30">
          <MobileMenuButton onClick={() => setMobileNavOpen(true)} />
          <div className="leading-tight text-center">
            <div className="text-[14px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-primary">.</span></div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Publisher portal</div>
          </div>
          <div className="w-9" />
        </div>
        <UnsignedContractBanner />
        <Outlet />
      </main>

      {/* Mobile-only nav surfaces */}
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        items={NAV}
        roleLabel="Publisher Portal"
        user={user}
        onLogout={handleLogout}
      />
      <MobileBottomNav items={BOTTOM_NAV} />

      <SupportChatWidget />
    </div>
  );
}
